export interface CragData {
  CragName: string;
  ParkingLocation: {
    Latitude: number;
    Longitude: number;
  };
}

export interface NavigationCoordinates {
  latitude: number;
  longitude: number;
  label: string;
}

/**
 * Find parking coordinates for a given crag or region
 * @param cragName - The crag name to search for
 * @param regionName - The region name to search for
 * @param cragsData - Array of crag data from crags.json
 * @returns Navigation coordinates or null if not found
 */
export function findParkingCoordinates(
  cragName: string,
  regionName: string,
  cragsData: CragData[]
): NavigationCoordinates | null {
  if (!cragsData || cragsData.length === 0) {
    return null;
  }

  // First, try to find exact crag match with various formats
  const exactCragMatch = cragsData.find(crag => {
    const cragNameLower = crag.CragName.toLowerCase();
    const searchCragLower = cragName.toLowerCase();
    const searchRegionLower = regionName.toLowerCase();
    
    return cragNameLower === searchCragLower || 
           cragNameLower === `${searchRegionLower} > ${searchCragLower}` ||
           cragNameLower === `${searchCragLower} > ${searchRegionLower}` ||
           cragNameLower.includes(searchCragLower) ||
           cragNameLower.includes(searchRegionLower);
  });

  if (exactCragMatch) {
    return {
      latitude: exactCragMatch.ParkingLocation.Latitude,
      longitude: exactCragMatch.ParkingLocation.Longitude,
      label: exactCragMatch.CragName
    };
  }

  // If no exact match, try to find region-level parking
  const regionMatch = cragsData.find(crag => {
    const cragNameLower = crag.CragName.toLowerCase();
    const searchRegionLower = regionName.toLowerCase();
    
    return cragNameLower === searchRegionLower ||
           cragNameLower.includes(searchRegionLower);
  });

  if (regionMatch) {
    return {
      latitude: regionMatch.ParkingLocation.Latitude,
      longitude: regionMatch.ParkingLocation.Longitude,
      label: regionMatch.CragName
    };
  }

  return null;
}

/**
 * Generate navigation URLs for different map services
 */
export function generateNavigationUrls(coordinates: NavigationCoordinates) {
  const { latitude, longitude, label } = coordinates;
  const encodedLabel = encodeURIComponent(label);

  return {
    googleMaps: `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}&travelmode=driving`,
    appleMaps: `https://maps.apple.com/?daddr=${latitude},${longitude}&dirflg=d`,
    waze: `https://waze.com/ul?ll=${latitude},${longitude}&navigate=yes`
  };
}
